import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ButtonRounded, TextInput } from 'is24-corecss';
import InfoPopOver from '../infoPopOver/InfoPopOver';
import veriffLogo from './veriff.svg';
import veriffDarkModeLogo from './veriff-dark.svg';
import {
  getVeriffVerificationSession,
  getVeriffVerificationSessionLightRegisteredUser,
} from '../../api/veriff/VeriffVerificationAttempt';
import { useClientConfig } from '../../utils/clientConfigContext';
import { VeriffSessionRequest } from '../../api/veriff/model/VeriffSessionRequest';
import { useTvsState } from '../../utils/useTvsState';
import { useVeriffSession } from '../../utils/veriffSessionContext';
import { ApiResponseError } from '../../api/ApiResponseError';
import { trackEvent } from '../../tracking/tracking';
import { validateFirstName, validateLastName } from '../../pages/verificationLandingPage/validators/Validators';
import { VeriffVerificationSession } from '../../api/veriff/model/VeriffVerificationSession';

interface VeriffVerificationProps {
  onContentChange: (content: React.ReactNode) => void;
  onError: (error: string) => void;
  isDarkModeOn?: boolean;
}

const VeriffVerification: React.FC<VeriffVerificationProps> = ({ onContentChange, onError, isDarkModeOn }) => {
  const { clientId, userFirstName, userLastName, nameEditable, nameCheckEnableForVeriff, userId } = useClientConfig();
  const [, setTvsState] = useTvsState();
  const { setSessionUrl } = useVeriffSession();
  const { t } = useTranslation();
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const isFieldsDisabled = nameEditable === false;
  const [firstName, setFirstName] = useState<string>(userFirstName || '');
  const [lastName, setLastName] = useState<string>(userLastName || '');
  const [invalidNameMessage, setInvalidNameMessage] = useState<string>('');
  const [invalidLastNameMessage, setInvalidLastNameMessage] = useState<string>('');

  const [pendingSessionData, setPendingSessionData] = useState<{ sessionUrl: string; sessionId: string } | null>(null);

  const validateUserInput = () => {
    const isFirstNameValid = validateFirstName(firstName);
    const isLastNameValid = validateLastName(lastName);
    setInvalidNameMessage(isFirstNameValid ? '' : t('VerificationLandingPage.firstNameError'));
    setInvalidLastNameMessage(isLastNameValid ? '' : t('VerificationLandingPage.lastNameError'));
    return isFirstNameValid && isLastNameValid;
  };

  const handleFirstInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value);
  };

  const handleLastInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
  };

  const setTvsSessionId = (tvsSessionId: string) => {
    setSearchParams(
      (prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set('tvs_session_id', tvsSessionId);
        return newParams;
      },
      { replace: true },
    );
  };

  // Effect to monitor URL parameter changes and complete the verification flow
  useEffect(() => {
    const currentSessionId = searchParams.get('tvs_session_id');

    if (currentSessionId && pendingSessionData && currentSessionId === pendingSessionData.sessionId) {
      setSessionUrl(pendingSessionData.sessionUrl);
      setTvsState('veriff_verification');
      setPendingSessionData(null);
    }
  }, [searchParams, pendingSessionData, setSessionUrl, setTvsState]);

  React.useEffect(() => {
    onContentChange(
      nameCheckEnableForVeriff ? (
        <div className='grid grid-flex gutter-horizontal-m margin-top-xxl'>
          <div className='grid-item one-half palm-one-whole palm-margin-bottom-l'>
            <TextInput
              data-testid='first-name-input'
              id='first-name'
              label={t('VerificationLandingPage.inputLabelFName')}
              placeholder=''
              mandatory={true}
              value={firstName}
              onChange={handleFirstInputChange}
              disabled={isFieldsDisabled}
              valid={invalidNameMessage.length === 0}
              captionText={invalidNameMessage}
            />
          </div>
          <div className='grid-item one-half palm-one-whole'>
            <TextInput
              data-testid='last-name-input'
              id='last-name'
              label={t('VerificationLandingPage.inputLabelLName')}
              placeholder=''
              value={lastName}
              mandatory={true}
              disabled={isFieldsDisabled}
              onChange={handleLastInputChange}
              valid={invalidLastNameMessage.length === 0}
              captionText={invalidLastNameMessage}
            />
          </div>
          <div className='grid grid-flex font-body-small-regular padding-left-m font-lightgray desk-margin-top-xxl lap-margin-top-xxl modal-bottom-text palm-margin-bottom-l  margin-top-xxl palm-margin-top-xl'>
            {t('VerificationLandingPage.subContent')}
          </div>
        </div>
      ) : (
        <div className='grid grid-flex font-body-small-regular font-lightgray desk-margin-top-xxl lap-margin-top-xxl modal-bottom-text palm-margin-bottom-l'>
          {t('VerificationLandingPage.subContent')}
        </div>
      ),
    );
  }, [t, onContentChange, firstName, lastName, invalidNameMessage, invalidLastNameMessage]);

  const onVeriffVerificationClick = () => {
    // If name check is enabled but validation fails, don't proceed
    if (nameCheckEnableForVeriff && !validateUserInput()) {
      return;
    }

    const veriffVerificationTrigger: VeriffSessionRequest = {
      triggeredBy: clientId,
      ...(nameCheckEnableForVeriff && {
        firstName,
        lastName,
      }),
    };

    setIsRequestPending(true);
    const verificationPromise = userId
      ? getVeriffVerificationSessionLightRegisteredUser(veriffVerificationTrigger, userId)
      : getVeriffVerificationSession(veriffVerificationTrigger);

    verificationPromise
      .then((response: VeriffVerificationSession) => {
        trackEvent('is24.de.sicherheit.verifizierung.modal', 'verification_started', 'user', clientId, 'veriff');

        setPendingSessionData({
          sessionUrl: response.sessionUrl,
          sessionId: response.sessionId,
        });

        setTvsSessionId(response.sessionId);
      })
      .catch((error: ApiResponseError) => {
        if (error.responseStatus === 401) {
          onError('errors.unauthorized');
        } else {
          onError('errors.default');
        }
      })
      .finally(() => {
        setIsRequestPending(false);
      });
  };

  return (
    <div className='modal-footer verification-modal-footer'>
      <div className='grid grid-flex'>
        <div className='grid-item grid-align-center one-half palm-one-whole'>
          <div className='grid grid-flex grid-align-center'>
            <p className='margin-right-xs font-lightgray font-body-small-regular'>
              {t('VerificationLandingPage.footerText')}
            </p>
            <img
              src={isDarkModeOn ? veriffDarkModeLogo : veriffLogo}
              alt='verification'
              className='footer-icon'
              height={16}
              width={49.48}
            />
            <InfoPopOver content={t(`InformationText.veriffPopOverContent`, { returnObjects: true })} />
          </div>
        </div>
        <div className='grid-item one-half palm-one-whole palm-margin-top-l'>
          <div className='grid-flex grid-justify-end verification-footer-div palm-one-whole'>
            <ButtonRounded
              data-testid='veriff-verification-button'
              onClick={onVeriffVerificationClick}
              className='verification-footer-button'
              loading={isRequestPending}
              label={t('VerificationLandingPage.buttonText')}
            ></ButtonRounded>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VeriffVerification;
